// Optimized Chatbot component with memoization and stable callbacks
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { IoMdSend, IoMdInformationCircleOutline } from "react-icons/io";
import { MdOutlineAttachment } from "react-icons/md";
import { useTranslation } from "react-i18next";
import type { ChatState, AiProfile } from "./chatState";
import { initialState } from "./chatState";
import TypingIndicator from "./TypingIndicator";
import {
  getCurrentUser,
  syncUserWithServer,
  updateUser,
  getCurrentOrganization,
} from "src/features/profile/state/profileSlice";
import MuiTooltip from "@mui/material/Tooltip";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  resetActiveGptConversation,
  updateChat,
  setActiveGptConversation,
  fetchGptConversations,
  setChosenLlmProvider,
  getChosenLlmProvider,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
  resetScenarioHelperRagAgentIds,
  startPlanModeChat,
  continuePlanModeChat,
  duplicatePlanThunk,
  transitionToReady,
  refinePlanThunk,
  getScenarioHelperContextPdfs,
  getScenarioHelperContextImages,
  getPlanModeProgress,
  getGenerationStartedAt,
  addScenarioHelperContextPdf,
  addScenarioHelperContextImage,
  setScenarioHelperContextImages,
  resetScenarioHelperContextPdfs,
  resetScenarioHelperContextImages,
} from "../state/chatbotSlice";
import ChatBubble from "./ChatBubble";
import GraphBubble from "./GraphBubble";
import type { IGptConversation, ScenariaReferenceImageChange } from "src/model/model";
import type { IMedia } from "src/model/unityObject";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import PlanPreview from "./PlanPreview";
import GenerationRecap from "./GenerationRecap";
import PlanModeProgress from "./PlanModeProgress";
import SkipIntakeButton from "./SkipIntakeButton";
import { canSkipIntake } from "../utils/skipThreshold";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { shallowEqual } from "react-redux";
import { GPT_CONVERSATION_LOADER_COMPONENT_ID } from "./GptConversationLoader";
import ConfigureGenerationPanel from "./configureGeneration/ConfigureGenerationPanel";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import GeneratePdfFromScenarioHelperResponse from "./GeneratePdfFromScenarioHelperResponse";
import { generateHashFromString } from "../utils/generateHashFromString";
import { isMarkdownShapedLikeAScenario } from "../utils/isMarkdownShapedLikeAScenario";
import { renderPlanToMarkdown } from "../utils/renderPlanToMarkdown";
import LlmProviderDropdown from "./LlmProviderDropdown";
import { useNavigate } from "react-router-dom";
import {
  setCurrentProject,
  setCurrentProjectPermissions,
  getProjectPermissionsFromServer,
} from "src/features/projects/state/projectsSlice";
import {
  fetchSpecificProjectGroupWithProjects,
  type IProjectGroupElementsFromCustomAPI,
} from "src/features/projectGroups/state/projectGroupsSlice";
import type { IProject } from "src/model/model";
import "./Chatbot.scss";
import getUserPreferredLlmProvider from "../utils/getUserPrefferedLlmProvider";
import { useDictation } from "src/features/tts-and-stt/hooks/useDictation";
import { useConversationMode } from "src/features/tts-and-stt/hooks/useConversationMode";
import MicrophoneButton from "src/features/tts-and-stt/components/MicrophoneButton";
import VoiceModeToggle from "src/features/tts-and-stt/components/VoiceModeToggle";
import ConversationStatusStrip from "src/features/chatbot/components/ConversationStatusStrip";
import AttachContextMedia, { isMediaProcessing } from "./AttachContextMedia";
import DecorStatusRow from "./referenceImage/DecorStatusRow";
import ReferenceImageGalleryHost, {
  openReferenceImageGallery,
} from "./referenceImage/ReferenceImageGalleryHost";
import useRetryImageAnalysis from "./referenceImage/useRetryImageAnalysis";
import { Tooltip } from "@mui/material";
import ContextSelector from "src/common/components/ContextSelector/ContextSelector";
import type { ScenariaOptionalLayer } from "../utils/scenariaContextPrefs";
import {
  getExcludedScenariaLayers,
  setExcludedScenariaLayers,
  SCENARIA_MANDATORY_LAYERS,
  SCENARIA_OPTIONAL_LAYERS,
} from "../utils/scenariaContextPrefs";

interface Message {
  text: string;
  sender: "bot" | "user";
}

interface ChatbotProps {
  mode: "scenarioHelper";
  activeConversation?: IGptConversation | null;
  onGenerateProject?: () => void;
}

export const CHATBOT_COMPONENT_ID = "chatbotComponentId";

const replaceReferenceImageConfirmId = "replaceReferenceImageConfirm";
const removeReferenceImageConfirmId = "removeReferenceImageConfirm";

const Chatbot: React.FC<ChatbotProps> = ({ mode, activeConversation, onGenerateProject }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpeningModule, setIsOpeningModule] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);

  const [chatState, setChatState] = useState<ChatState>(initialState);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState("");
  const [inputMode, setInputMode] = useState(true);
  // Inline (non-modal) personal-context editor, mirroring the /profile/me
  // "Profil IA" block — freeform text, edited on the go, no modal.
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editMetier, setEditMetier] = useState("");
  const [editTargetAudience, setEditTargetAudience] = useState("");
  const [editSector, setEditSector] = useState("");
  const [editDescription, setEditDescription] = useState("");
  // Session-scoped, on-the-fly copy of the org's "Profil IA". Seeded from the
  // org's persisted ai_context, freely editable by ANY creator (incl.
  // non-admins) to shape the assistant for THIS design session only. It is
  // never written back to the org — not even for admins, who must use the
  // /profile organization view to change the org default — and lives only in
  // component state → lost on refresh, and reset whenever the active
  // conversation changes (clean slate per design session).
  const [orgAiContextOverride, setOrgAiContextOverride] = useState<{
    sector?: string;
    description?: string;
  } | null>(null);
  const [sessionConversationId, setSessionConversationId] = useState<number | null>(null);
  // The decor-row/provenance-row image whose "Retirer" was clicked, awaiting
  // the removal confirm.
  const [pendingRemoveReferenceImage, setPendingRemoveReferenceImage] = useState<IMedia | null>(
    null,
  );
  // The gallery pick made while another reference image is already integrated,
  // awaiting the replace confirm (the modal names both files). Picking with no
  // existing image integrates immediately — no pending state involved.
  const [pendingReplaceReferenceImage, setPendingReplaceReferenceImage] = useState<IMedia | null>(
    null,
  );
  // True while THIS surface's reference-image picking session owns the shared
  // "modal-gallery" modal — gates the ReferenceImageGalleryHost mount (see its
  // one-instance-at-a-time contract).
  const [isRefPicking, setIsRefPicking] = useState(false);
  // Target of the decor-realign refine turn in flight — "image" for an
  // add/replace, "ai" for a removal, null when idle. Drives the dedicated
  // status-strip line AND the decor rows' optimistic radio + spinner chip.
  const [realigningDecorTo, setRealigningDecorTo] = useState<"image" | "ai" | null>(null);
  const isRealigningDecor = realigningDecorTo !== null;

  const bottomRef = useRef<HTMLDivElement>(null);

  const user = useAppSelector(getCurrentUser, shallowEqual);
  const organization = useAppSelector(getCurrentOrganization);
  const chosenLlmProvider = useAppSelector(getChosenLlmProvider);
  const ragAgentIds = useAppSelector(getScenarioHelperRagAgentIds, shallowEqual);
  const contextPdfs = useAppSelector(getScenarioHelperContextPdfs);
  const contextImages = useAppSelector(getScenarioHelperContextImages);
  const planProgress = useAppSelector(getPlanModeProgress);
  const generationStartedAt = useAppSelector(getGenerationStartedAt);
  const phase = activeConversation?.plan_phase ?? null;
  const plan = activeConversation?.conversation_metadata?.plan;
  const isPlanMode = phase !== null && phase !== undefined;
  // Legacy / orphan plan-mode conversations have plan_phase === null.
  // The OG-flow chat is read-/replay-only with the deprecated
  // "Générer le module" markdown shortcut; the 17 plan-mode orphans
  // (plan in metadata, phase nulled by earlier dev iteration) are
  // effectively stuck. Either way, nudge the user to start fresh.
  const isLegacyConversation = Boolean(
    mode === "scenarioHelper" && activeConversation && !activeConversation.plan_phase,
  );
  const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(false);
  // Which surface fills the content region while in plan-mode. A single
  // discriminated union — future stages are new variants, never parallel
  // booleans (D2, docs/plans/todo/scenaria-configure-generation-stage.md).
  // Local (not Redux): leaving the page remounts Chatbot and resets to thread,
  // which is the desired behavior.
  const [uiStage, setUiStage] = useState<"thread" | "configure">("thread");
  // Reset the dismissal whenever the user switches to a different
  // conversation — otherwise dismissing once permanently hides the banner
  // for every legacy conversation in this session.
  useEffect(() => {
    setLegacyBannerDismissed(false);
    setUiStage("thread");
    // A pending reference-image confirm belongs to the conversation it was
    // opened against — never let it leak into a newly switched-to one.
    setPendingReplaceReferenceImage(null);
    setPendingRemoveReferenceImage(null);
    setIsRefPicking(false);
  }, [activeConversation?.id, dispatch]);
  // The Configure stage only exists while the plan is ready to generate. Any
  // other phase (a refinement pushed back to intake, or generation kicking off)
  // forces the surface back to the thread so the panel can never linger.
  useEffect(() => {
    if (phase !== "ready_to_generate") {
      setUiStage("thread");
    }
  }, [phase]);
  // Ref tracks the latest phase so async submit handlers always read fresh
  // state instead of stale useCallback closures.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;
  const [showIntakeHistory, setShowIntakeHistory] = useState(false);
  const [intakeHistoryBoundary, setIntakeHistoryBoundary] = useState<number | null>(null);
  const lastConvIdForBoundaryRef = useRef<number | undefined>(undefined);
  // Tracks the conversation id the session org-profile override belongs to, so
  // we only drop it on a genuine switch between two existing conversations —
  // not on the per-generation object refresh (same id, new reference).
  const lastConvIdForOverrideRef = useRef<number | null>(null);
  // Set synchronously when the session sends the first message of a brand-new
  // conversation. `sessionConversationId` is only known after the server
  // responds, but `activeConversation.id` flips to the new id earlier (via the
  // mid-thunk syncUserWithServer), so the override-reset effect below would
  // otherwise see a null→newId transition with `sessionConversationId` still
  // null and wipe the user's edit. This ref lets the effect recognise that the
  // new conversation is the session's own before its id is resolved.
  const sessionStartedNewConvRef = useRef(false);

  const hasMediaProcessing = useMemo(
    () => [...contextPdfs, ...contextImages].some(isMediaProcessing),
    [contextPdfs, contextImages],
  );

  const isSubmitDisabled = useMemo(
    () => !userInput.trim() || isSubmitting || hasMediaProcessing,
    [userInput, isSubmitting, hasMediaProcessing],
  );

  // Every disabled branch gets a reason, ordered so the actionable ones win over
  // the generic "type something": an empty box is the least useful thing we can
  // say while a file is still processing. Gated on isSubmitDisabled so an
  // *enabled* button never carries a tooltip explaining why it can't be clicked.
  const submitDisabledReason = !isSubmitDisabled
    ? ""
    : hasMediaProcessing
    ? t("pages.chat.fileProcessingReason")
    : isSubmitting
    ? t("pages.chat.submitInFlightReason")
    : t("pages.chat.emptyInputReason");

  // Refs bridge the cyclic dep between conversation (needs to call dictation.startListening)
  // and dictation (needs to call submit-with-text in auto-send path).
  const dictationRef = useRef<ReturnType<typeof useDictation> | null>(null);
  const submitWithTextRef = useRef<((text: string) => Promise<void>) | null>(null);

  const conversation = useConversationMode({
    onAutoListenCue: () => {
      dictationRef.current?.startListening().catch(() => undefined);
    },
    onDeactivate: () => {
      dictationRef.current?.stopListening().catch(() => undefined);
    },
  });

  const dictation = useDictation({
    // Conversation mode wants a short silence timeout so the user's reply auto-sends;
    // dictation mode wants a long one so thinking pauses don't cut the user off.
    silenceTimeout: conversation.voiceModeEnabled ? 1500 : 30000,
    onTranscript: (text, { viaTimeout }) => {
      if (conversation.voiceModeEnabled && viaTimeout && text.trim()) {
        submitWithTextRef.current?.(text).catch(() => undefined);
      } else {
        setUserInput(text);
      }
    },
  });
  dictationRef.current = dictation;

  // Mirror live transcript into userInput while listening so the textarea
  // has a single source of truth and never shows stale state.
  useEffect(() => {
    if (!dictation.isListening) return;
    const live = [dictation.transcript, dictation.interimTranscript].filter(Boolean).join(" ");
    setUserInput(live);
  }, [dictation.isListening, dictation.transcript, dictation.interimTranscript]);

  // Track whether a TTS streaming session is open for the current bot reply.
  const ttsSessionOpenRef = useRef(false);

  const getSavedAiProfile = useCallback((): AiProfile | null => {
    try {
      const prefs = (user as any)?.preferences;
      if (!prefs) return null;
      const obj = typeof prefs === "string" ? JSON.parse(prefs) : prefs;
      const profile = obj?.aiProfile;
      if (profile?.metier) return profile;
      return null;
    } catch {
      return null;
    }
  }, [user]);

  const orgCtx = useMemo(() => {
    // The session override (when set) is the source of truth for what the
    // assistant will actually receive this turn; fall back to the persisted
    // org ai_context otherwise.
    const ctx = orgAiContextOverride ?? (organization as any)?.ai_context;
    if (!ctx || typeof ctx !== "object") return null;
    return ctx.sector || ctx.description ? ctx : null;
  }, [orgAiContextOverride, organization]);

  const hasUserProfile = useMemo(() => !!getSavedAiProfile()?.metier, [getSavedAiProfile]);

  const aiContextParts = useMemo(() => {
    const parts: string[] = [];
    const profile = getSavedAiProfile();
    if (profile?.metier) parts.push(profile.metier);
    if (profile?.targetAudience) parts.push(profile.targetAudience);
    if (orgCtx?.sector) parts.push(orgCtx.sector);
    if (orgCtx?.description) parts.push(orgCtx.description);
    return parts;
  }, [getSavedAiProfile, orgCtx]);

  // Initialize conversation on mode or activeConversation change. Keyed on
  // the id (not the whole object) so in-place mutations of the active
  // conversation — e.g. refinePlanThunk.fulfilled pushing a new plan/prompt,
  // or a reference-image change — don't re-trigger a full reload. That
  // reload re-hydrates contextImages from source_media_with_context, a field
  // reference-image-change turns never update on the local object, which was
  // silently reverting the optimistic setScenarioHelperContextImages update
  // right after a successful add/replace/remove.
  useEffect(() => {
    if (mode === "scenarioHelper") {
      if (activeConversation) {
        loadPreviousConversation();
      } else {
        resetChatLocally();
      }
    } else {
      resetChatLocally();
    }
  }, [mode, activeConversation?.id]);

  // Drop the session-scoped org-profile override whenever the user moves onto a
  // *pre-existing* conversation — switching between saved conversations, or
  // opening one from history while on a blank slate. The single transition we
  // preserve is the session's own first message turning the draft into a brand
  // new conversation: that id matches `sessionConversationId`, and the edits
  // made before the first turn must carry through the rest of the session.
  // Keying on the id (not the whole activeConversation object) means a
  // per-generation refresh — same id, new object reference pushed by
  // syncUserWithServer/fetchGptConversations — never reaches here, so it no
  // longer wipes the user's in-session edits. Leaving the Scenaria view
  // remounts this component, resetting the override via its initial state —
  // the expected "reset to org default" on re-entry.
  useEffect(() => {
    const prevId = lastConvIdForOverrideRef.current;
    const currId = activeConversation?.id ?? null;
    // The session's own first message turns a blank draft into a brand-new
    // conversation. `sessionConversationId` confirms this once the server id is
    // known, but it lags behind `activeConversation.id`; the synchronously-set
    // `sessionStartedNewConvRef` covers that gap so the very first generation no
    // longer wipes the override.
    const isSessionOwnNewConversation =
      prevId === null &&
      currId !== null &&
      (currId === sessionConversationId || sessionStartedNewConvRef.current);
    if (isSessionOwnNewConversation) {
      sessionStartedNewConvRef.current = false;
    }
    if (currId !== null && currId !== prevId && !isSessionOwnNewConversation) {
      setOrgAiContextOverride(null);
    }
    lastConvIdForOverrideRef.current = currId;
  }, [activeConversation?.id, sessionConversationId]);

  // Scroll chat into view
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  // Hydrate the chatbot slice from the user's last-known active conversation,
  // but ONLY when the slice holds none — the frontend owns *which* conversation
  // is active (same identity guard as syncUserWithServer in chatbotSlice).
  // user.active_gpt_conversation is snapshotted at login, never re-fetched
  // mid-session, and only ever points at the last conversation the user
  // *started*; adopting it whenever it differs hijacked deliberate selections.
  // The customer-visible symptom: "Voir la conversation d'origine" sets the
  // module's own conversation and navigates here, then this effect fired on
  // mount and replaced it — so every module opened the same stale history.
  // Keeping the no-op when a conversation is already active also preserves the
  // original intent (in-session plan-mode transitions must not be reverted to
  // the login-time plan_phase on remount).
  useEffect(() => {
    if (
      user?.active_gpt_conversation?.id &&
      user.active_gpt_conversation.chat_origin === "scenarioHelper" &&
      !activeConversationRef.current
    ) {
      dispatch(setActiveGptConversation(user.active_gpt_conversation));
    }
  }, [user?.active_gpt_conversation?.id, dispatch]);

  // Set initial LLM provider
  useEffect(() => {
    const initial = getUserPreferredLlmProvider(user?.preferences);
    dispatch(setChosenLlmProvider(initial));
  }, [user?.preferences, dispatch]);

  const loadPreviousConversation = useCallback(() => {
    if (!activeConversation) return;
    setIsEditingProfile(false);
    const { conversation_metadata, conversation_data } = activeConversation;
    setChatState((prev) => ({ ...prev, userFormResults: conversation_metadata.userFormResults }));
    const msgs: Message[] = [];
    const { userPrompts } = conversation_metadata;
    const { output } = conversation_data;
    const total = Math.max(userPrompts.length, output.length);
    for (let i = 0; i < total; i++) {
      if (i < userPrompts.length) msgs.push({ text: userPrompts[i], sender: "user" });
      if (i < output.length) msgs.push({ text: output[i], sender: "bot" });
    }
    const isSameConv = lastConvIdForBoundaryRef.current === activeConversation.id;
    lastConvIdForBoundaryRef.current = activeConversation.id;
    setMessages(msgs);

    if (activeConversation.plan_phase && activeConversation.plan_phase !== "intake") {
      const persistedBoundary = activeConversation.conversation_metadata?.intake_boundary;
      if (typeof persistedBoundary === "number") {
        // Server pinned the boundary at consolidation time. Trust it —
        // refinement turns append on both sides but the boundary stays put.
        setIntakeHistoryBoundary(persistedBoundary);
      } else {
        // Legacy conversation (created before intake_boundary was persisted).
        // Within the same conv, keep the previously-set boundary so refinement
        // turns are revealed correctly. On a fresh load of a legacy conv,
        // pin at the current message count (the imperfection: refinement
        // turns made between server-side persistence and this load are
        // collapsed too — but the next refinement self-heals via the
        // controller's defensive backfill).
        setIntakeHistoryBoundary((prev) => (isSameConv && prev !== null ? prev : msgs.length));
      }
    } else {
      setIntakeHistoryBoundary(null);
    }
    setInputMode(true);

    // Hydrate attachments from server-side source_media_with_context (A5 + C10).
    // Server is source of truth; clear local first, then partition by media type.
    dispatch(resetScenarioHelperContextPdfs());
    dispatch(resetScenarioHelperContextImages());
    const sourceMedia = activeConversation.source_media_with_context || [];
    sourceMedia.forEach((m) => {
      const isImage = (m?.type || "").toLowerCase().startsWith("image");
      if (isImage) {
        dispatch(addScenarioHelperContextImage(m));
      } else {
        dispatch(addScenarioHelperContextPdf(m));
      }
    });

    // Restore RAG agent IDs from conversation metadata, or clear selection
    const storedRagAgentIds = activeConversation.conversation_metadata?.rag_agent_ids;
    if (Array.isArray(storedRagAgentIds) && storedRagAgentIds.length > 0) {
      dispatch(setScenarioHelperRagAgentIds(storedRagAgentIds));
    } else {
      dispatch(resetScenarioHelperRagAgentIds());
    }
  }, [activeConversation, dispatch]);

  // Direct URL navigation to /courses/:pg/list/:project is unsupported by the
  // app: every other entry point pre-hydrates `selectedProjectGroup` /
  // `currentProject` / project permissions before navigating (see
  // ProjectsList.tsx:94-102). Mirror that pattern here so the success card's
  // "Voir le module" CTA lands on the scenario graph with the right state.
  const handleOpenGeneratedModule = useCallback(async () => {
    const pgId = activeConversation?.lastGeneratedProjectGroupId;
    const projectId = activeConversation?.lastGeneratedProjectId;
    if (!pgId || isOpeningModule) return;
    setIsOpeningModule(true);
    try {
      const pgRes = await dispatch(
        fetchSpecificProjectGroupWithProjects({
          projectGroupId: pgId,
          componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
        }),
      ).unwrap();
      const pg = pgRes?.data;
      const project = projectId
        ? pg?.project_group_elements?.find(
            (pge: IProjectGroupElementsFromCustomAPI) =>
              Number(pge.project?.id) === Number(projectId),
          )
            ?.project
        : undefined;
      if (project && projectId) {
        dispatch(setCurrentProject(project as unknown as IProject));
        const permRes = await dispatch(
          getProjectPermissionsFromServer({
            projectId: Number(projectId),
            componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
          }),
        );
        dispatch(setCurrentProjectPermissions((permRes.payload as any)?.data ?? []));
        navigate(`/courses/${pgId}/list/${projectId}/graph`);
      } else {
        navigate(`/courses/${pgId}/list`);
      }
    } catch {
      dispatch(
        setAlert({
          msg: t("pages.chat.openModuleError"),
          type: "warning",
        }),
      );
    } finally {
      setIsOpeningModule(false);
    }
  }, [activeConversation, dispatch, isOpeningModule, navigate, t]);

  const handleRestartFromPlan = useCallback(async () => {
    if (!activeConversation?.id || isRestarting) return;
    setIsRestarting(true);
    const sourceName = activeConversation.name;
    try {
      const res = await dispatch(duplicatePlanThunk({ conversationId: activeConversation.id }));
      if ("error" in res && res.error) {
        dispatch(
          setAlert({
            type: "error",
            msg: t("pages.chat.generationRecap.restartFailed"),
          }),
        );
      } else {
        // The thread swap is abrupt (reducer sets the duplicate active and the
        // [mode, activeConversation?.id] effect rebuilds onto the plan card) —
        // the toast is what tells the user what just happened.
        dispatch(
          setAlert({
            type: "success",
            msg: t("pages.chat.generationRecap.restartSuccess", { name: sourceName }),
          }),
        );
      }
    } finally {
      setIsRestarting(false);
    }
  }, [activeConversation?.id, activeConversation?.name, isRestarting, dispatch, t]);

  const resetChatLocally = useCallback(() => {
    setChatState(initialState);
    setIsBotTyping(false);
    setUserInput("");
    setSessionConversationId(null);
    sessionStartedNewConvRef.current = false;

    // scenarioHelper mode
    dispatch(resetActiveGptConversation());
    dispatch(resetScenarioHelperRagAgentIds());
    dispatch(resetScenarioHelperContextPdfs());
    dispatch(resetScenarioHelperContextImages());

    setMessages([{ text: t("pages.chat.scenarioHelperOpeningMessage"), sender: "bot" }]);
    setInputMode(true);

    setIsEditingProfile(false);
    const savedProfile = getSavedAiProfile();
    if (savedProfile?.metier) {
      // Profile already saved — inject into state silently
      setChatState((prev) => ({
        ...prev,
        userFormResults: {
          metier: savedProfile.metier,
          targetAudience: savedProfile.targetAudience,
        },
      }));
    }
    // No saved profile → no forced modal. The strip nudge invites the user
    // to fill it via the inline editor whenever they want.
  }, [mode, dispatch, getSavedAiProfile, t]);

  const updateBotMessage = useCallback(
    (chunk: string) => {
      setMessages((prev) => {
        const msgs = [...prev];
        const i = msgs.length - 1;
        msgs[i] = { ...msgs[i], text: msgs[i].text + chunk };
        return msgs;
      });
      if (ttsSessionOpenRef.current) {
        conversation.pushSpeakingChunk(chunk);
      }
    },
    [conversation],
  );

  const saveAiProfileToPreferences = useCallback(
    (formResults: { metier?: string; targetAudience?: string }) => {
      if (!user?.id || !formResults.metier) return;
      try {
        const prefs = (user as any)?.preferences;
        const base =
          typeof prefs === "string"
            ? JSON.parse(prefs) ?? {}
            : typeof prefs === "object" && prefs !== null
            ? { ...prefs }
            : {};
        base.aiProfile = {
          metier: formResults.metier,
          targetAudience: formResults.targetAudience,
        };
        void dispatch(
          updateUser({
            id: Number(user.id),
            preferences: base,
          } as any),
        );
      } catch (e) {
        console.error("Failed to save AI profile:", e);
      }
    },
    [user, dispatch],
  );

  // Inline editing of the user's own AI profile (métier / public cible)
  // straight from the scenaria — freeform, no modal, mirroring the
  // /profile/me "Profil IA" block. No admin gate: this writes the user's
  // own preferences, so a non-admin can always edit their own AI context.
  const openProfileEditor = useCallback(() => {
    const saved = getSavedAiProfile();
    setEditMetier(saved?.metier ?? "");
    setEditTargetAudience(saved?.targetAudience ?? "");
    // Seed from the in-session override if the user already tweaked it this
    // session, otherwise from the org's persisted ai_context.
    const ctx = orgAiContextOverride ?? (organization as any)?.ai_context;
    setEditSector(ctx?.sector ?? "");
    setEditDescription(ctx?.description ?? "");
    setIsEditingProfile(true);
  }, [getSavedAiProfile, orgAiContextOverride, organization]);

  const cancelProfileEditor = useCallback(() => {
    setIsEditingProfile(false);
  }, []);

  // "Éditer" toggles the inline editor: re-clicking it closes it, exactly
  // like "Annuler".
  const toggleProfileEditor = useCallback(() => {
    if (isEditingProfile) {
      setIsEditingProfile(false);
    } else {
      openProfileEditor();
    }
  }, [isEditingProfile, openProfileEditor]);

  const saveProfileEditor = useCallback(() => {
    const metier = editMetier.trim();
    if (!metier) return;
    const formResults = {
      metier,
      targetAudience: editTargetAudience.trim() || undefined,
    };

    // Only the user's own "Profil IA" (métier / public) is actually persisted
    // below. The "Profil édité avec succès" confirmation must reflect THAT —
    // not org-context tweaks, which are session-only. Detect a real change vs
    // the persisted profile so editing just the org buffer fires no alert.
    const saved = getSavedAiProfile();
    const profilePersistedChange =
      (saved?.metier ?? "") !== metier ||
      (saved?.targetAudience ?? "") !== (formResults.targetAudience ?? "");

    setChatState((prev) => ({ ...prev, userFormResults: formResults }));
    saveAiProfileToPreferences(formResults);

    // Org-level fields are ALWAYS session-only here — for every creator,
    // admins included. The edit only lands in the session-scoped override
    // (what the assistant receives this session), never written back to the
    // org, lost on refresh / conversation switch. Changing the org's default
    // "Profil IA" permanently is done exclusively from the /profile
    // organization view. The session badge reflects this buffer change on its
    // own, so no alert is needed when only the org context moved.
    const sector = editSector.trim() || undefined;
    const description = editDescription.trim() || undefined;
    setOrgAiContextOverride({ sector, description });

    setIsEditingProfile(false);
    if (profilePersistedChange) {
      dispatch(setAlert({ type: "success", msg: t("alerts.profileEditSuccess") }));
    }
  }, [
    editMetier,
    editTargetAudience,
    editSector,
    editDescription,
    getSavedAiProfile,
    saveAiProfileToPreferences,
    dispatch,
    t,
  ]);

  // Shared post-refine handling for every ready-phase refinement turn (typed
  // message OR reference-image change): surfaces the error, the vision-failure
  // warning and the mediaType-flip warning. Returns false when the turn failed
  // so callers can bail without duplicating alert logic.
  const processRefineResult = useCallback(
    (result: any, errorFallbackMsg: string): boolean => {
      if (result?.error) {
        dispatch(
          setAlert({
            type: "error",
            msg: result?.payload || errorFallbackMsg,
            customTimeoutInMs: 5000,
          }),
        );
        return false;
      }
      const refinedPlan = result?.payload?.plan;
      const failedImages = (refinedPlan?.attachedImages || []).filter((img: any) => img?.failed);
      if (failedImages.length > 0) {
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.chat.imageAnalysisFailedUpdated"),
            customTimeoutInMs: 5000,
          }),
        );
      }
      const mediaTypeOverrides = (result?.payload?.mediaTypeOverrides ?? []) as Array<{
        sceneTitle: string;
        previousMediaType: "image360" | "video360";
        mediaType: "image360" | "video360";
      }>;
      const switchesToVideo = mediaTypeOverrides.filter((o) => o.mediaType === "video360");
      if (switchesToVideo.length > 0) {
        const titles = switchesToVideo.map((o) => `"${o.sceneTitle}"`).join(", ");
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.chat.video360Warning", {
              count: switchesToVideo.length,
              titles,
            }),
            customTimeoutInMs: 8000,
          }),
        );
      }
      return true;
    },
    [dispatch, t],
  );

  const handleUserPromptSubmitWithText = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      const prompt = text;
      setUserInput("");
      setIsSubmitting(true);
      setMessages((prev) => [
        ...prev,
        { text: prompt, sender: "user" },
        { text: "", sender: "bot" },
      ]);
      setIsBotTyping(true);

      // Open the TTS streaming session BEFORE the LLM call so the WS handshake
      // overlaps with the bot's first tokens. Chunks are pushed live via
      // updateBotMessage; the session is closed in the finally block.
      if (conversation.voiceModeEnabled) {
        ttsSessionOpenRef.current = true;
        conversation.beginSpeakingSession().catch((err) => {
          console.error("Voice mode session error:", err);
          ttsSessionOpenRef.current = false;
        });
      }

      try {
        if (mode === "scenarioHelper") {
          const conversationId = activeConversationRef.current?.id ?? sessionConversationId;
          const currentPhase = phaseRef.current;
          const sourceMediaIds = [
            ...contextPdfs.map((m) => Number(m.id)),
            ...contextImages.map((m) => Number(m.id)),
          ];

          if (!conversationId) {
            // New conversation → always start in plan mode (D2). Flag the
            // session as owning the conversation that's about to be created so
            // the override-reset effect preserves any in-session edits through
            // the null→newId transition (see sessionStartedNewConvRef).
            sessionStartedNewConvRef.current = true;
            const result = await dispatch(
              startPlanModeChat({
                userPrompt: prompt,
                userFormResults: chatState.userFormResults,
                sourceMediasWithContext: sourceMediaIds,
                chosenLlmProvider,
                ragAgentIds,
                excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
                orgAiContextOverride: orgAiContextOverride ?? undefined,
                sourceMedias: [...contextPdfs, ...contextImages],
                cb: updateBotMessage,
                cbOnRequestEnd: () => {
                  void dispatch(
                    fetchGptConversations({
                      componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
                      start: 0,
                    }),
                  );
                  void dispatch(syncUserWithServer({}));
                },
              }),
            );
            const created = result.payload as IGptConversation | undefined;
            if (created?.id) {
              setSessionConversationId(created.id);
            }
          } else if (currentPhase === "intake") {
            await dispatch(
              continuePlanModeChat({
                userPrompt: prompt,
                conversationId,
                ragAgentIds,
                excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
                orgAiContextOverride: orgAiContextOverride ?? undefined,
                sourceMediasWithContext: sourceMediaIds,
                cb: updateBotMessage,
              }),
            );
            void dispatch(syncUserWithServer({}));
          } else if (currentPhase === "ready_to_generate") {
            // Typed messages here are ALWAYS refinement turns. The plan →
            // generation transition is button-only (the "Générer le module"
            // button below the plan / the GraphBubble button) — we never
            // parse chat text to open the generation modal, so phrases like
            // "de façon générale" can't misfire a phase change.
            // Refinement turn — JSON response, not streamed; show the final
            // server confirmation as the bot reply.
            const result = (await dispatch(
              refinePlanThunk({
                conversationId,
                message: prompt,
                ragAgentIds,
                excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
                orgAiContextOverride: orgAiContextOverride ?? undefined,
              }),
            )) as any;
            if (!processRefineResult(result, t("pages.chat.refinementFailed"))) {
              updateBotMessage(t("pages.chat.refinementFailed"));
              return;
            }
            // The reducer pushes "__PLAN__" to output and the user message
            // to userPrompts. loadPreviousConversation rebuilds the local
            // messages array from Redux, rendering the new plan inline.
            // Push the sentinel to local messages immediately so the user
            // sees the updated plan without waiting for the useEffect cycle.
            setMessages((prev) => {
              const msgs = [...prev];
              // Replace the empty bot bubble (pushed at submit time) with
              // the plan sentinel so renderedMessages picks it up.
              const lastIdx = msgs.length - 1;
              if (lastIdx >= 0 && msgs[lastIdx].sender === "bot") {
                msgs[lastIdx] = { text: "__PLAN__", sender: "bot" };
              }
              return msgs;
            });
          } else if (currentPhase === null) {
            await dispatch(
              updateChat({
                userPrompt: prompt,
                conversationId,
                ragAgentIds,
                cb: updateBotMessage,
              }),
            );
            void dispatch(syncUserWithServer({}));
          } else {
            // generating / generated — input is disabled at the UI layer.
          }
        }
      } finally {
        if (ttsSessionOpenRef.current) {
          conversation.endSpeakingSession();
          ttsSessionOpenRef.current = false;
        }
        setIsSubmitting(false);
        setIsBotTyping(false);
      }
    },
    [
      activeConversation,
      sessionConversationId,
      chatState.userFormResults,
      dispatch,
      mode,
      chosenLlmProvider,
      ragAgentIds,
      contextPdfs,
      contextImages,
      updateBotMessage,
      conversation,
      orgAiContextOverride,
      processRefineResult,
      t,
    ],
  );

  const handleUserPromptSubmit = useCallback(() => {
    // User-initiated commit (Enter / Send): treat as "I'm done — go."
    // If the mic is live (either mode), stop it and drop the accumulated buffer
    // so onEnd won't fire a second submit through the auto-send path. userInput
    // is already mirroring the live transcript via the effect above, so the
    // captured value is the right thing to submit.
    if (dictation.isListening) {
      dictation.resetTranscript();
      dictation.stopListening().catch(() => undefined);
    }
    if (conversation.voiceModeEnabled) {
      // Also disarm the pending auto-listen flag and silence any in-flight TTS.
      conversation.disarmAutoListen();
      conversation.stopSpeaking();
    }
    return handleUserPromptSubmitWithText(userInput);
  }, [userInput, handleUserPromptSubmitWithText, conversation, dictation]);

  // Wire the auto-send path: dictation's onTranscript (closing over an outdated
  // submit) reads the current submit fn through this ref each time it fires.
  submitWithTextRef.current = handleUserPromptSubmitWithText;

  // Reference-image attach/replace/remove at the Plan stage. One transactional
  // /refine call links/unlinks the media, re-describes it and realigns the
  // decor; `syncedImages` is the post-change attachment list to mirror into
  // redux once the server has confirmed (never before).
  const fireReferenceImageChange = useCallback(
    async (change: ScenariaReferenceImageChange, syncedImages: IMedia[]) => {
      if (isSubmitting) return;
      const conversationId = activeConversationRef.current?.id ?? sessionConversationId;
      if (!conversationId) return;
      setIsSubmitting(true);
      setIsBotTyping(true);
      setRealigningDecorTo(change.addMediaId ? "image" : "ai");
      try {
        const result = (await dispatch(
          refinePlanThunk({
            conversationId,
            // Ignored by the server in this mode — it authors both the LLM
            // instruction and the thread line it persists.
            message: "",
            ragAgentIds,
            excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
            orgAiContextOverride: orgAiContextOverride ?? undefined,
            referenceImageChange: change,
          }),
        )) as any;
        if (!processRefineResult(result, t("pages.chat.referenceImageChangeFailed"))) return;
        // The user may have switched away from this conversation while the
        // request was in flight — never let its result overwrite the
        // (unrelated) conversation now on screen.
        if (activeConversationRef.current?.id === conversationId) {
          dispatch(setScenarioHelperContextImages(syncedImages));
        }
        setPendingReplaceReferenceImage(null);
      } finally {
        setIsSubmitting(false);
        setIsBotTyping(false);
        setRealigningDecorTo(null);
      }
    },
    [
      isSubmitting,
      sessionConversationId,
      dispatch,
      ragAgentIds,
      user?.id,
      orgAiContextOverride,
      processRefineResult,
      t,
    ],
  );

  // Failed-vision retry, shared by the plan-card decor row (the Options
  // provenance row runs its own instance so it can also bust the preview cache).
  const { isRetrying: isRetryingAnalysis, retry: retryAnalysis } = useRetryImageAnalysis(
    activeConversation?.id,
  );

  // Opens the shared reference-image gallery. Every entry point (composer
  // attach button, plan-card decor row, Options provenance row) lands here, so
  // picking behaves identically wherever it starts.
  const handlePickReferenceImage = useCallback(() => {
    if (isSubmitting) return;
    setIsRefPicking(true);
    openReferenceImageGallery(dispatch);
  }, [isSubmitting, dispatch]);

  // A gallery pick IS the integration intent: with no reference image it fires
  // the decor realign immediately; with one already integrated it routes
  // through the replace confirm (the modal names both files).
  const handlePickedReferenceImage = useCallback(
    (media: IMedia) => {
      if (isSubmitting) return;
      if (contextImages.length > 0) {
        setPendingReplaceReferenceImage(media);
        dispatch(openStandardModal(replaceReferenceImageConfirmId));
      } else {
        void fireReferenceImageChange({ addMediaId: Number(media.id) }, [media]);
      }
    },
    [isSubmitting, contextImages, dispatch, fireReferenceImageChange],
  );

  // "Retirer" on a decor/provenance row — confirmation first, the decor of
  // every scene gets reinvented without the image (never a silent unlink).
  const handleRemoveReferenceImage = useCallback(
    (media: IMedia) => {
      if (isSubmitting) return;
      setPendingRemoveReferenceImage(media);
      dispatch(openStandardModal(removeReferenceImageConfirmId));
    },
    [isSubmitting, dispatch],
  );

  const handleEnterConfigure = useCallback(() => {
    setUiStage("configure");
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (userInput.trim() && !isSubmitting) void handleUserPromptSubmit();
      }
    },
    [userInput, isSubmitting, handleUserPromptSubmit],
  );

  const visibleMessages = useMemo(() => {
    if (isPlanMode && phase !== "intake" && intakeHistoryBoundary !== null && !showIntakeHistory) {
      return messages.slice(intakeHistoryBoundary);
    }
    return messages;
  }, [messages, isPlanMode, phase, intakeHistoryBoundary, showIntakeHistory]);

  // Find the index of the last __PLAN__ marker so we can show the CTA
  // only on the most recent plan.
  const lastPlanIdx = useMemo(() => {
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      if (visibleMessages[i].sender === "bot" && visibleMessages[i].text === "__PLAN__") {
        return i;
      }
    }
    return -1;
  }, [visibleMessages]);

  const renderedMessages = useMemo(
    () =>
      visibleMessages.map((message, idx) => {
        // Plan sentinel: render PlanPreview inline instead of a text bubble.
        if (message.sender === "bot" && message.text === "__PLAN__") {
          const isLatestPlan = idx === lastPlanIdx;
          return (
            <div key={idx}>
              {isLatestPlan && plan ? (
                <div className="shrink-0 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                  {/* Decor provenance band — the permanent, affirmative answer
                      to "is my image driving the 360° decor?". Server truth
                      only: a gallery pick integrates right away. */}
                  <DecorStatusRow
                    plan={plan}
                    contextImages={contextImages}
                    busy={isSubmitting}
                    onPick={phase === "ready_to_generate" ? handlePickReferenceImage : undefined}
                    onRemove={
                      phase === "ready_to_generate" ? handleRemoveReferenceImage : undefined
                    }
                    onRetryAnalysis={phase === "ready_to_generate" ? retryAnalysis : undefined}
                    isRetryingAnalysis={isRetryingAnalysis}
                    realigningTo={realigningDecorTo}
                    className="px-3 py-2 bg-purple-50/40 border-b border-gray-100"
                  />
                  <PlanPreview plan={plan} />
                  <div className="px-3 pb-3 pt-1">
                    <GraphBubble
                      markdown={renderPlanToMarkdown(plan)}
                      mdStringId={`plan-${activeConversation?.id ?? "x"}-${idx}`}
                      flat
                    />
                  </div>
                  {phase === "ready_to_generate" && (
                    <div className="px-3 pb-3 pt-2 flex flex-col items-center gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 italic">
                        <IoMdInformationCircleOutline className="shrink-0" />
                        <span>{t("pages.chat.readyToGenerateHint")}</span>
                      </div>
                      <button
                        className="btn-primary-fill text-sm"
                        disabled={isSubmitting}
                        onClick={() =>
                          isPlanMode || !onGenerateProject
                            ? handleEnterConfigure()
                            : onGenerateProject()
                        }
                      >
                        {t("pages.chat.generateModule")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex justify-start">
                  <div className="px-3 py-1.5 rounded-md bg-gray-100 border border-gray-200 text-xs text-gray-400 italic">
                    {t("pages.chat.previousPlanReplaced")}
                  </div>
                </div>
              )}
            </div>
          );
        }

        // Skip empty user prompts (system-inserted for plan consolidation).
        if (message.sender === "user" && !message.text.trim()) {
          return null;
        }

        return (
          <div key={idx}>
            <ChatBubble customKey={idx} sender={message.sender} text={message.text} />
            {!isPlanMode && isMarkdownShapedLikeAScenario(message.text) && (
              <>
                <GraphBubble
                  markdown={message.text}
                  mdStringId={generateHashFromString(message.text)}
                />
                {!isSubmitting && (
                  <div className="flex w-full justify-center">
                    {message.text && (
                      <GeneratePdfFromScenarioHelperResponse
                        markdown={message.text}
                        key={`${message.text}-${idx}`}
                      />
                    )}
                    <button
                      className="btn-primary-outline mt-1 w-3/5"
                      onClick={() => onGenerateProject?.()}
                    >
                      {t("pages.chat.generateModuleFromGraph")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }),
    [
      visibleMessages,
      lastPlanIdx,
      plan,
      phase,
      isSubmitting,
      dispatch,
      t,
      isPlanMode,
      onGenerateProject,
      contextImages,
      handlePickReferenceImage,
      handleRemoveReferenceImage,
      handleEnterConfigure,
      retryAnalysis,
      isRetryingAnalysis,
      realigningDecorTo,
      activeConversation?.id,
    ],
  );

  // Skip-intake threshold (R2). Backend mirrors this rule.
  const typedUserTurns = useMemo(
    () => messages.filter((m) => m.sender === "user" && m.text.trim().length > 0).length,
    [messages],
  );
  const totalUserTurns = useMemo(
    () => messages.filter((m) => m.sender === "user").length,
    [messages],
  );
  const attachmentCount = contextPdfs.length + contextImages.length;
  const skipState = useMemo(
    () => canSkipIntake({ typedUserTurns, totalUserTurns, attachmentCount }),
    [typedUserTurns, totalUserTurns, attachmentCount],
  );

  const handleSkipIntake = useCallback(async () => {
    if (!activeConversation?.id) return;
    setIsBotTyping(true);
    setIsSubmitting(true);
    try {
      const result = (await dispatch(
        transitionToReady({ conversationId: activeConversation.id, ragAgentIds }),
      )) as any;
      if (result?.error) {
        dispatch(
          setAlert({
            type: "error",
            msg: result?.payload || t("pages.chat.planConsolidationFailed"),
            customTimeoutInMs: 5000,
          }),
        );
        return;
      }
      const planResult = result?.payload?.plan;
      const failedImages = (planResult?.attachedImages || []).filter((img: any) => img?.failed);
      if (failedImages.length > 0) {
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.chat.imageAnalysisFailedGenerated"),
            customTimeoutInMs: 5000,
          }),
        );
      }
      // The reducer pushes the ""/"__PLAN__" pair to the Redux conversation,
      // but the reload effect is keyed on the conversation id so local messages
      // never pick it up in-session. Mirror the pair here (same as the
      // refinement turn does) so the plan renders inline at the end of the
      // thread — where a reload puts it — instead of falling back to the
      // legacy block above the whole conversation. Empty user prompts are
      // skipped by renderedMessages.
      setMessages((prev) => [
        ...prev,
        { text: "", sender: "user" },
        { text: "__PLAN__", sender: "bot" },
      ]);
      // Boundary is persisted by the server inside transitionToReady;
      // loadPreviousConversation reads it back via syncUserWithServer.
    } finally {
      setIsBotTyping(false);
      setIsSubmitting(false);
    }
  }, [activeConversation?.id, dispatch, ragAgentIds, t]);

  // The Configure stage takes over the chat-window + composer region (D2). The
  // stepper, AI-context strip and legacy banner above it stay mounted so the
  // takeover reads as the same region changing content, not a new window.
  const isConfigureStage =
    mode === "scenarioHelper" && phase === "ready_to_generate" && uiStage === "configure";

  return (
    <div className="flex h-full">
      <div className="flex m-2 w-full flex-col items-center">
        <div className="w-full h-full p-4 bg-white shadow-lg rounded-lg flex flex-col relative">
          {mode === "scenarioHelper" && (
            <div className="flex items-center gap-3 flex-wrap mb-2 px-1 text-xs text-gray-500">
              <ContextSelector
                className="relative"
                i18nBase="pages.chat.contextSelector"
                mandatoryLayers={SCENARIA_MANDATORY_LAYERS}
                optionalLayers={SCENARIA_OPTIONAL_LAYERS}
                sourceKey={user?.id}
                disabled={!user?.id}
                prefs={{
                  getExcluded: () => (user?.id ? getExcludedScenariaLayers(user.id) : []),
                  setExcluded: (e) => {
                    if (user?.id) {
                      setExcludedScenariaLayers(user.id, e as ScenariaOptionalLayer[]);
                    }
                  },
                }}
              />
              <span className="flex-1 min-w-0 truncate">
                {aiContextParts.length > 0 && (
                  <>
                    {t("pages.chat.aiContext")} {aiContextParts.join(" · ")}
                    {(!orgCtx || !hasUserProfile) && <span className="ml-2 text-gray-400">—</span>}
                  </>
                )}
                {!hasUserProfile && !orgCtx && (
                  <span className="text-amber-500">
                    {t("pages.chat.aiContextHintBoth")}{" "}
                    <button
                      type="button"
                      onClick={openProfileEditor}
                      className="underline hover:text-amber-600"
                    >
                      {t("pages.chat.aiContextHintProfile")}
                    </button>{" "}
                    {t("pages.chat.aiContextHintAnd")} {t("pages.chat.aiContextHintOrg")}{" "}
                    {t("pages.chat.aiContextHintSuffix")}
                  </span>
                )}
                {hasUserProfile && !orgCtx && (
                  <span className="text-amber-500">{t("pages.chat.aiContextOrgHint")}</span>
                )}
                {!hasUserProfile && orgCtx && (
                  <button
                    type="button"
                    onClick={openProfileEditor}
                    className="text-amber-500 hover:text-amber-600"
                  >
                    {t("pages.chat.aiContextProfileHint")}
                  </button>
                )}
              </span>
              {(hasUserProfile || orgCtx) && (
                <button
                  type="button"
                  onClick={toggleProfileEditor}
                  className="shrink-0 text-purple-500 hover:text-purple-600 font-medium"
                >
                  {t("general.edit")}
                </button>
              )}
            </div>
          )}
          {mode === "scenarioHelper" && isEditingProfile && (
            <div className="mb-2 px-1">
              <div className="rounded-md border border-gray-200 bg-white shadow-sm p-3 text-xs">
                <div className="font-medium text-gray-700 mb-2">
                  {t("pages.profile.main.aiProfileTitle")}
                </div>
                <label className="block mb-2">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.main.aiMetier")}
                  </span>
                  <input
                    type="text"
                    value={editMetier}
                    onChange={(e) => setEditMetier(e.target.value)}
                    placeholder={t("pages.profile.main.aiMetierPlaceholder") as string}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    autoFocus
                  />
                </label>
                <label className="block mb-3">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.main.aiTargetAudience")}
                  </span>
                  <input
                    type="text"
                    value={editTargetAudience}
                    onChange={(e) => setEditTargetAudience(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editMetier.trim()) {
                        e.preventDefault();
                        saveProfileEditor();
                      }
                    }}
                    placeholder={t("pages.profile.main.aiTargetAudiencePlaceholder") as string}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </label>

                <div className="flex items-center gap-1.5 mb-2 pt-2 border-t border-gray-100">
                  <span className="font-medium text-gray-700">
                    {t("pages.profile.organization.aiContextTitle")}
                  </span>
                  <MuiTooltip
                    title={t("pages.chat.aiOrgFieldsSessionHint") as string}
                    arrow
                    placement="top"
                  >
                    <span className="text-gray-400 cursor-help inline-flex">
                      <IoMdInformationCircleOutline className="w-3.5 h-3.5" />
                    </span>
                  </MuiTooltip>
                </div>
                <label className="block mb-2">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.organization.aiSector")}
                  </span>
                  <input
                    type="text"
                    value={editSector}
                    onChange={(e) => setEditSector(e.target.value)}
                    placeholder={t("pages.profile.organization.aiSectorPlaceholder") as string}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </label>
                <label className="block mb-3">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.organization.aiDescription")}
                  </span>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder={t("pages.profile.organization.aiDescriptionPlaceholder") as string}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </label>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-alternative-outline text-sm"
                    onClick={cancelProfileEditor}
                  >
                    {t("general.cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn-primary-fill text-sm"
                    onClick={saveProfileEditor}
                    disabled={!editMetier.trim()}
                  >
                    {t("general.save")}
                  </button>
                </div>
              </div>
            </div>
          )}
          {mode === "scenarioHelper" && isPlanMode && (
            <PlanModeProgress
              step={
                phase === "intake"
                  ? 1
                  : phase === "ready_to_generate"
                  ? uiStage === "configure"
                    ? 3
                    : 2
                  : phase === "generating"
                  ? 4
                  : phase === "generated"
                  ? 5
                  : 2
              }
            />
          )}
          {isLegacyConversation && !legacyBannerDismissed && (
            <div className="flex items-start gap-3 px-3 py-2 mb-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900">
              <div className="flex-1 text-xs leading-relaxed">
                <div className="font-semibold mb-0.5">{t("pages.chat.legacyBannerTitle")}</div>
                <div>
                  {t("pages.chat.legacyBannerBody")}{" "}
                  <button
                    type="button"
                    className="text-purple-500 hover:text-purple-600 font-medium underline"
                    onClick={resetChatLocally}
                  >
                    {t("pages.chat.legacyBannerLink")}
                  </button>
                  .
                </div>
              </div>
              <button
                type="button"
                aria-label={t("pages.chat.legacyBannerDismiss")}
                className="text-amber-700 hover:text-amber-900 text-lg leading-none px-1"
                onClick={() => setLegacyBannerDismissed(true)}
              >
                ×
              </button>
            </div>
          )}
          {isConfigureStage && activeConversation?.id ? (
            <ConfigureGenerationPanel
              plan={plan}
              conversationId={activeConversation.id}
              ragAgentIds={ragAgentIds}
              onBack={() => setUiStage("thread")}
              onGenerationStarted={() => setUiStage("thread")}
              isReferenceImageBusy={isSubmitting}
              referenceImageRealigningTo={realigningDecorTo}
              onPickReferenceImage={handlePickReferenceImage}
              onRemoveReferenceImage={handleRemoveReferenceImage}
            />
          ) : (
            <>
              <div className="chat-window bg-gray-100 p-4 flex-grow overflow-y-scroll rounded-md flex flex-col space-y-4">
                {mode === "scenarioHelper" &&
                  isPlanMode &&
                  phase !== "intake" &&
                  intakeHistoryBoundary !== null && (
                    <button
                      onClick={() => setShowIntakeHistory((v) => !v)}
                      className="text-xs text-gray-500 hover:text-gray-700 underline self-start"
                    >
                      {showIntakeHistory
                        ? t("pages.chat.hideIntakeHistory")
                        : t("pages.chat.showIntakeHistory")}
                    </button>
                  )}
                {/* Context documents inventory. The reference image is NOT
                    listed here anymore: it is a decor driver, not a knowledge
                    attachment, and its first-class home is the plan card's
                    decor band (provenance toggle + thumbnail + Changer). */}
                {mode === "scenarioHelper" &&
                  isPlanMode &&
                  phase !== "intake" &&
                  contextPdfs.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-md bg-gray-200/60 text-[11px] text-gray-500">
                      <MdOutlineAttachment size={13} className="shrink-0" />
                      {contextPdfs.map((pdf) => (
                        <span key={pdf.id} className="truncate max-w-[140px]">
                          {pdf.filename || pdf.name}
                        </span>
                      ))}
                    </div>
                  )}
                {/* Fallback for legacy conversations consolidated before __PLAN__
                sentinel was introduced — render the current plan inline. */}
                {mode === "scenarioHelper" &&
                  isPlanMode &&
                  phase === "ready_to_generate" &&
                  plan &&
                  lastPlanIdx === -1 && (
                    <div className="shrink-0 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                      <DecorStatusRow
                        plan={plan}
                        contextImages={contextImages}
                        busy={isSubmitting}
                        onPick={handlePickReferenceImage}
                        onRemove={handleRemoveReferenceImage}
                        onRetryAnalysis={retryAnalysis}
                        isRetryingAnalysis={isRetryingAnalysis}
                        realigningTo={realigningDecorTo}
                        className="px-3 py-2 bg-purple-50/40 border-b border-gray-100"
                      />
                      <PlanPreview plan={plan} />
                      <div className="px-3 pb-3 pt-1">
                        <GraphBubble
                          markdown={renderPlanToMarkdown(plan)}
                          mdStringId={`plan-fallback-${activeConversation?.id ?? "x"}`}
                          flat
                        />
                      </div>
                      <div className="px-3 pb-3 pt-2 flex flex-col items-center gap-2">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 italic">
                          <IoMdInformationCircleOutline className="shrink-0" />
                          <span>{t("pages.chat.readyToGenerateHint")}</span>
                        </div>
                        <button
                          className="btn-primary-fill text-sm"
                          disabled={isSubmitting}
                          onClick={() =>
                            isPlanMode || !onGenerateProject
                              ? handleEnterConfigure()
                              : onGenerateProject()
                          }
                        >
                          {t("pages.chat.generateModule")}
                        </button>
                      </div>
                    </div>
                  )}
                {renderedMessages}
                {isBotTyping && (
                  <div className="flex justify-start">
                    <div className="px-4 py-3 rounded-xl rounded-bl-sm bg-gray-50 border border-gray-200 text-gray-500">
                      <TypingIndicator />
                    </div>
                  </div>
                )}
                {mode === "scenarioHelper" && phase === "intake" && activeConversation?.id && (
                  <SkipIntakeButton
                    canSkip={skipState.canSkip}
                    reason={skipState.reason}
                    isSubmitting={isSubmitting}
                    onClick={handleSkipIntake}
                  />
                )}
                {mode === "scenarioHelper" &&
                  (phase === "generating" || phase === "generated") &&
                  plan && (
                    <GenerationRecap
                      plan={plan}
                      phase={phase}
                      progressMessage={planProgress?.message ?? null}
                      sourceMedia={[...contextImages, ...contextPdfs]}
                      lastGeneratedProjectGroupId={activeConversation?.lastGeneratedProjectGroupId}
                      isOpeningModule={isOpeningModule}
                      onOpenModule={handleOpenGeneratedModule}
                      onNewScenario={resetChatLocally}
                      onRestartFromPlan={handleRestartFromPlan}
                      isRestarting={isRestarting}
                      generationStartedAt={generationStartedAt}
                    />
                  )}
                {mode === "scenarioHelper" &&
                  !activeConversation &&
                  messages.length <= 1 &&
                  !isBotTyping && (
                    <div className="flex-1 flex items-center justify-center">
                      <p className="text-sm text-gray-400 text-center max-w-md">
                        {t("pages.chat.scenarioHelperEmptyHint")}
                      </p>
                    </div>
                  )}
                {/* Inline form buttons removed — onboarding modal used instead */}
                <div ref={bottomRef} />
              </div>
              {inputMode && (
                <div className="flex-shrink-0 border-t border-gray-200 px-4 pt-3 pb-2 bg-white">
                  {/* Unified status strip: one predictable, always-rendered row that
                  surfaces speaking / thinking / listening / plan-progress with
                  a single visual paradigm. Sits above the controls row so the
                  user always knows the current turn state without hunting. The
                  decor-realign line rides the planMessage slot: it is the most
                  specific thing we can say while that turn runs. */}
                  <ConversationStatusStrip
                    isSpeaking={conversation.isSpeaking}
                    onStopSpeaking={conversation.stopSpeaking}
                    isThinking={isBotTyping || isSubmitting}
                    isListening={dictation.isListening}
                    hasSpeechStarted={dictation.lastActivityAt !== null}
                    planMessage={
                      mode === "scenarioHelper"
                        ? isRealigningDecor
                          ? t("pages.chat.decorRealignInProgress")
                          : planProgress?.message ?? null
                        : null
                    }
                    scenarioPhase={mode === "scenarioHelper" ? phase ?? null : null}
                    hasMediaProcessing={hasMediaProcessing}
                  />
                  {/* Single row: attach controls (scenarioHelper only) on the
                  left, VoiceModeToggle on the right. items-start keeps the
                  toggle anchored at the top when chips stack. */}
                  <div className="flex items-start justify-between gap-3 mb-1.5 min-h-[28px]">
                    <div className="flex-1 min-w-0">
                      {mode === "scenarioHelper" && (
                        <AttachContextMedia
                          isEditionEnabled={(!phase || phase === "intake") && !isSubmitting}
                          isImagePickBusy={isSubmitting}
                          hideDecorImages={realigningDecorTo === "ai"}
                          onImageButtonOverride={
                            phase === "ready_to_generate" ? handlePickReferenceImage : undefined
                          }
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <VoiceModeToggle
                        enabled={conversation.voiceModeEnabled}
                        onToggle={conversation.toggleVoiceMode}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 items-end">
                    <textarea
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={1}
                      readOnly={dictation.isListening}
                      className={`flex-1 resize-none rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 disabled:text-gray-400 transition-all overflow-y-auto max-h-40 ${
                        dictation.isListening
                          ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                      }`}
                      placeholder={
                        phase === "generated"
                          ? t("pages.chat.inputPlaceholderGenerated")
                          : phase === "generating"
                          ? t("pages.chat.inputPlaceholderGenerating")
                          : phase === "ready_to_generate"
                          ? t("pages.chat.inputPlaceholderRefine")
                          : t("pages.chat.scenarioHelperInputPlaceholder")
                      }
                      disabled={isSubmitting || phase === "generating" || phase === "generated"}
                    />
                    <MicrophoneButton
                      isListening={dictation.isListening}
                      isSupported={dictation.isSupported}
                      disabled={isSubmitting}
                      idleTooltipKey={
                        conversation.voiceModeEnabled
                          ? "voice.conversationStartTooltip"
                          : "voice.dictateTooltip"
                      }
                      listeningTooltipKey={
                        conversation.voiceModeEnabled
                          ? "voice.conversationListeningTooltip"
                          : "voice.dictateListeningTooltip"
                      }
                      listeningWaitingTooltipKey={
                        conversation.voiceModeEnabled
                          ? "voice.conversationListeningWaitingTooltip"
                          : "voice.dictateListeningWaitingTooltip"
                      }
                      cooldown={
                        conversation.voiceModeEnabled
                          ? { triggerAt: dictation.lastActivityAt, durationMs: 1500 }
                          : undefined
                      }
                      onStartListening={() => {
                        // Barge-in: if the bot is speaking and conversation mode is on,
                        // stop TTS so the mic can take over cleanly.
                        if (conversation.voiceModeEnabled && conversation.isSpeaking) {
                          conversation.stopSpeaking();
                        }
                        dictation.resetTranscript();
                        dictation
                          .startListening()
                          .catch((err) => console.error("STT start error:", err));
                      }}
                      onStopListening={() => {
                        // Re-click while listening = stop only, no send (both modes).
                        // Disarm the conversation auto-listen cue so a stale flag
                        // doesn't re-arm the mic later.
                        conversation.disarmAutoListen();
                        dictation.stopListening().catch(() => undefined);
                      }}
                    />
                    <Tooltip
                      title={submitDisabledReason}
                      disableHoverListener={!submitDisabledReason}
                    >
                      <span>
                        <button
                          onClick={handleUserPromptSubmit}
                          disabled={isSubmitDisabled}
                          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-600 rounded-xl transition-colors"
                        >
                          <IoMdSend size={16} />
                        </button>
                      </span>
                    </Tooltip>
                  </div>
                  <div className="flex justify-between items-center mt-1.5">
                    {mode === "scenarioHelper" && (
                      <LlmProviderDropdown
                        defaultValue={chosenLlmProvider}
                        onChange={(value) => dispatch(setChosenLlmProvider(value))}
                        disabled={Boolean(activeConversation) || isSubmitting}
                      />
                    )}
                    <p className="text-xs text-gray-400 ml-auto">
                      {t("pages.chat.shiftEnterHint")}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <IsLoading componentId={CHATBOT_COMPONENT_ID} showSuccess={false} />

      {/* Reference-image replace confirm — only opened when an image is already
          attached, so the user knows which file is being swapped out. */}
      <ModalStandard id={replaceReferenceImageConfirmId} modalType="standard" className="w-[28rem]">
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">
            {t("pages.chat.replaceReferenceImageConfirmBody", {
              oldName: contextImages[0]?.filename || contextImages[0]?.name || "",
              newName:
                pendingReplaceReferenceImage?.filename || pendingReplaceReferenceImage?.name || "",
            })}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => {
                dispatch(closeStandardModal(replaceReferenceImageConfirmId));
                setPendingReplaceReferenceImage(null);
              }}
            >
              {t("general.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isSubmitting}
              onClick={() => {
                dispatch(closeStandardModal(replaceReferenceImageConfirmId));
                if (!pendingReplaceReferenceImage) return;
                void fireReferenceImageChange(
                  {
                    addMediaId: Number(pendingReplaceReferenceImage.id),
                    removeMediaIds: contextImages.map((i) => Number(i.id)),
                  },
                  [pendingReplaceReferenceImage],
                );
              }}
            >
              {t("general.confirm")}
            </button>
          </div>
        </div>
      </ModalStandard>

      {/* Reference-image removal confirm — the decor of every scene gets
          reinvented without it, so this is never a silent unlink. */}
      <ModalStandard id={removeReferenceImageConfirmId} modalType="standard" className="w-[28rem]">
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">{t("pages.chat.removeReferenceImageConfirmBody")}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => {
                dispatch(closeStandardModal(removeReferenceImageConfirmId));
                setPendingRemoveReferenceImage(null);
              }}
            >
              {t("general.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isSubmitting}
              onClick={() => {
                dispatch(closeStandardModal(removeReferenceImageConfirmId));
                if (!pendingRemoveReferenceImage) return;
                const removedId = Number(pendingRemoveReferenceImage.id);
                setPendingRemoveReferenceImage(null);
                void fireReferenceImageChange(
                  { removeMediaIds: [removedId] },
                  contextImages.filter((i) => Number(i.id) !== removedId),
                );
              }}
            >
              {t("general.confirm")}
            </button>
          </div>
        </div>
      </ModalStandard>

      {/* Single gallery instance behind every reference-image entry point —
          composer attach button, plan-card decor row, Options provenance row. */}
      <ReferenceImageGalleryHost
        open={isRefPicking}
        onClosed={() => setIsRefPicking(false)}
        onPicked={handlePickedReferenceImage}
      />

      {/* Onboarding modal removed — inline overlay used instead */}
    </div>
  );
};

export default React.memo(Chatbot);
